/**
 * Leitura do roteiro congelado de uma execução: em que passo e item está,
 * qual o próximo e o que cada toque (Feito, Voltar, Pular, Ir para) vira em
 * eventos. Os eventos daqui são os mesmos que o backend recebe.
 */
import { novaChave } from "./editor";
import { STATUS, TIPO, projetar, unidadesDoRoteiro } from "./projecao";

/** Estado projetado com os eventos do servidor + os que ainda estão na fila. */
export function estadoLocal(execucao, pendentes = []) {
  const unidades = unidadesDoRoteiro(execucao.roteiro);
  const chaves = unidades.map((u) => u.chave);
  const eventos = [...(execucao.eventos || []), ...pendentes.map((ev) => ({ ...ev, recebido_em: null }))];
  return { unidades, estado: projetar(chaves, eventos) };
}

function passoDaChave(roteiro, chaveUnidade) {
  if (!chaveUnidade) return null;
  const chavePasso = chaveUnidade.split("/")[0];
  return roteiro.passos.find((p) => p.chave === chavePasso) || null;
}

/** Onde a execução está: passo, item do sub-checklist e contagens para a tela. */
export function posicao(roteiro, unidades, estado) {
  const passo = passoDaChave(roteiro, estado.atual);
  const indicePasso = passo ? roteiro.passos.indexOf(passo) : roteiro.passos.length;
  const itens = passo?.itens || [];
  const item = itens.find((i) => i.chave === estado.atual) || null;
  const feitosNoPasso = itens.filter((i) => estado.unidades[i.chave]).length;
  return {
    passo,
    indicePasso,
    numero: passo ? passo.numero : roteiro.passos.length,
    total: roteiro.passos.length,
    item,
    itemIndice: item ? itens.indexOf(item) : -1,
    feitosNoPasso,
    proximo: roteiro.passos[indicePasso + 1] || null,
    passosConcluidos: roteiro.passos.filter((p) => unidadesDoPasso(unidades, p.chave).every((u) => estado.unidades[u])).length,
  };
}

export function unidadesDoPasso(unidades, chavePasso) {
  return unidades.filter((u) => u.passo === chavePasso).map((u) => u.chave);
}

/** Estado de cada passo para o índice: feito, pulado, atual ou a fazer. */
export function situacaoDoPasso(unidades, estado, passo) {
  const chaves = unidadesDoPasso(unidades, passo.chave);
  if (chaves.includes(estado.atual)) return "atual";
  const marcas = chaves.map((c) => estado.unidades[c]?.estado);
  if (marcas.every(Boolean)) return marcas.every((m) => m === TIPO.PULADO) ? "pulado" : "feito";
  return "a_fazer";
}

function evento(tipo, unidade = "", extra = {}) {
  return { id: novaChave(), tipo, unidade, ocorrido_em: new Date().toISOString(), ...extra };
}

/** Feito na unidade atual. */
export function eventosDeFeito(estado) {
  return estado.atual ? [evento(TIPO.FEITO, estado.atual)] : [];
}

/** Voltar: a unidade anterior à atual volta a "a fazer". */
export function eventosDeVoltar(unidades, estado) {
  const chaves = unidades.map((u) => u.chave);
  const indice = estado.atual ? chaves.indexOf(estado.atual) : chaves.length;
  const anterior = [...chaves.slice(0, indice)].reverse().find((c) => estado.unidades[c]);
  return anterior ? [evento(TIPO.DESFEITO, anterior)] : [];
}

/**
 * Ir para o passo `alvo` (pelo índice, com confirmação): para frente, os
 * passos do meio ficam "pulados"; para trás, do alvo até o atual voltam a
 * "a fazer".
 */
export function eventosDeIrPara(unidades, estado, chavePassoAlvo) {
  const chaves = unidades.map((u) => u.chave);
  const alvo = unidades.findIndex((u) => u.passo === chavePassoAlvo);
  const atual = estado.atual ? chaves.indexOf(estado.atual) : chaves.length;
  if (alvo < 0 || alvo === atual) return [];
  if (alvo > atual) {
    return chaves.slice(atual, alvo).filter((c) => !estado.unidades[c]).map((c) => evento(TIPO.PULADO, c));
  }
  return chaves.slice(alvo, atual).filter((c) => estado.unidades[c]).map((c) => evento(TIPO.DESFEITO, c));
}

export function eventoDePausa(motivo) {
  return [evento(TIPO.PAUSOU, "", { motivo: motivo || "" })];
}

export function eventoDeRetomada() {
  return [evento(TIPO.RETOMOU)];
}

export function eventoDeCancelamento(motivo) {
  return [evento(TIPO.CANCELOU, "", { motivo })];
}

export const encerrada = (estado) => estado.status === STATUS.CONCLUIDA || estado.status === STATUS.CANCELADA;
